import { useState } from 'react';
import { Avatar } from '../../components/Avatar.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Modal } from '../../components/Overlay.tsx';
import { StatusPill, PriorityPill, GhostPill } from '../../components/Pill.tsx';
import { ErrorState, Loading } from '../../components/DataState.tsx';
import { ApiError } from '../../api/client.ts';
import { addFriend, useFriend } from '../../api/rooms.ts';
import type { Person } from '../../domain.ts';
import { cardMeta } from '../book/shared.ts';

/**
 * D2 · D2-1 — 친구 팝업.
 *
 * **두 곳에서 열립니다.** 친구 리스트(D1)에서 이름을 눌렀을 때와, 모임
 * 기록지(F4)에서 구성원의 사진 · 이름을 눌렀을 때입니다. 구성원은 그룹장의
 * 친구 목록에서 고르므로 **구성원끼리는 아직 친구가 아닐 수 있고**, 그때
 * 대표 기도제목 자리에 친구 추가 안내가 들어갑니다 (D2-1).
 *
 * 둘은 같은 크기 · 같은 자리입니다. 갈리는 것은 아래 버튼 하나뿐입니다 —
 * 친구면 「친구 관리 · 함께 기도하기」, 아니면 「친구 추가」.
 *
 * 크기가 고정된 것은 기획서 2.1.1 이 명시한 것입니다. 머리는 고정이고
 * 대표 기도제목 부분만 안에서 스크롤됩니다.
 */

interface Props {
  person: Person;
  /** 어느 모임에서 열렸는지 — 「청년 3순 구성원」처럼 부제에 적습니다 */
  context?: string;
  onClose: () => void;
  onManage?: (person: Person) => void;
  /** 친구가 되면 부모의 목록도 다시 읽어야 합니다 */
  onAdded?: () => void;
}

export function FriendPopup({ person, context, onClose, onManage, onAdded }: Props) {
  const { data: friend, loading, error, reload } = useFriend(person.userId);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const name = friend?.name ?? person.name ?? '이름 없음';
  const mutual = friend?.mutual === true;
  const notes = friend?.representative ?? [];

  const add = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await addFriend(person.userId);
      // 팝업은 닫지 않습니다 — 추가하면 그 자리에서 대표 기도 노트가 보이는
      // D2 모습으로 바뀌는 것이 설계입니다
      reload();
      onAdded?.();
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '친구로 추가하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      label={`${name}의 대표 기도 노트`}
      onClose={onClose}
      footer={
        mutual ? (
          <>
            {onManage && (
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => onManage(person)}
              >
                친구 관리
              </button>
            )}
            {/* 모임 · 공유가 붙기 전까지 누를 곳이 없습니다 */}
            <button type="button" className="btn btn--primary" disabled>
              함께 기도하기
            </button>
          </>
        ) : (
          <button type="button" className="btn btn--primary" disabled={busy} onClick={add}>
            <Icon name="userPlus" size={16} />
            {busy ? '추가 중…' : '친구 추가'}
          </button>
        )
      }
    >
      <div className="modal__head">
        <Avatar name={name} seed={person.userId} />
        <p className="modal__name">{name}</p>
        {/* 친구면 대표 개수, 아니면 어디서 만난 사람인지 */}
        {mutual ? (
          <GhostPill>대표 기도제목 {notes.length}</GhostPill>
        ) : (
          context && <GhostPill>{context}</GhostPill>
        )}
      </div>

      {/* 머리는 고정, 이 부분만 스크롤 — 기획서 2.1.1 */}
      <div className="modal__body">
        {loading && <Loading label="불러오는 중" />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {failure && <ErrorState message={failure} onRetry={() => setFailure(null)} />}

        {!loading && !error && !mutual && (
          /* D2-1 — 아직 친구가 아닐 때. 대표 기도제목 자리에 안내가 들어갑니다 */
          <div className="empty">
            <span className="empty__icon">
              <Icon name="partner" size={21} />
            </span>
            <p className="empty__title">아직 친구가 아닙니다</p>
            <p className="empty__hint">
              친구로 추가하면
              <br />
              {name}의 대표 기도제목을 볼 수 있습니다.
            </p>
          </div>
        )}

        {!loading && mutual && notes.length === 0 && (
          <div className="empty">
            <p className="empty__title">아직 대표 기도제목이 없습니다</p>
            <p className="empty__hint">{name}이 대표로 지정하면 여기 보입니다.</p>
          </div>
        )}

        {/* 남의 노트라 읽기 전용입니다 — 설정 버튼이 없습니다 */}
        {notes.map((n) => (
          <article className="card" key={n.id}>
            <div className="card__pills">
              <StatusPill status={n.status} />
              {n.priority !== 'NORMAL' && <PriorityPill priority={n.priority} />}
            </div>
            <h3 className="card__title">{n.title}</h3>
            {n.body !== '' && <p className="card__text">{n.body}</p>}
            <p className="card__meta">{cardMeta(n)}</p>
          </article>
        ))}
      </div>
    </Modal>
  );
}
